import { useMemo, useState } from "react";
import toast, { Toaster } from "react-hot-toast";
import {
  AiOutlineCheckCircle,
  AiOutlineDelete,
  AiOutlineEdit,
  AiOutlineEnvironment,
  AiOutlinePlus,
} from "react-icons/ai";
import Nav from "../../components/home/Nav";
import AccountList from "../../components/home/AccountList";
import Spinner from "../../components/Spinner";
import {
  useCreateAddressMutation,
  useDeleteAddressMutation,
  useSetDefaultAddressMutation,
  useUpdateAddressMutation,
  useUserAddressesQuery,
} from "../../store/services/checkoutService";
import { toPersianNumber } from "../../utils/persian";

const splitFullName = (value = "") => {
  const parts = String(value)
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  if (parts.length <= 1) {
    return {
      firstName: parts[0] || "",
      lastName: ""
    };
  }

  const lastName = parts.pop();

  return {
    firstName: parts.join(" "),
    lastName
  };
};

const emptyForm = {
  receiver_first_name: "",
  receiver_last_name: "",
  receiver_mobile: "",
  province: "",
  city: "",
  address: "",
  postal_code: "",
  plaque: "",
  unit: "",
  description: "",
  is_default: false,
};

const UserAddresses = () => {
  const { data, isLoading, isFetching, isError } = useUserAddressesQuery();
  const [createAddress, createResponse] = useCreateAddressMutation();
  const [updateAddress, updateResponse] = useUpdateAddressMutation();
  const [deleteAddress, deleteResponse] = useDeleteAddressMutation();
  const [setDefaultAddress, defaultResponse] = useSetDefaultAddressMutation();

  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [formOpen, setFormOpen] = useState(false);

  const addresses = useMemo(() => data?.addresses || [], [data?.addresses]);
  const saving = createResponse.isLoading || updateResponse.isLoading;
  const busy = saving || deleteResponse.isLoading || defaultResponse.isLoading;

  const change = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setFormOpen(false);
  };

  const editAddress = (address) => {
    const {
      firstName,
      lastName
    } = splitFullName(address.receiver_name || "");

    setEditingId(address.id);

    setForm({
      receiver_first_name: firstName,
      receiver_last_name: lastName,
      receiver_mobile: address.receiver_mobile || "",
      province: address.province || "",
      city: address.city || "",
      address: address.address || "",
      postal_code: address.postal_code || "",
      plaque: address.plaque || "",
      unit: address.unit || "",
      description: address.description || "",
      is_default: Boolean(address.is_default),
    });
    setFormOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async (e) => {
    e.preventDefault();

    const firstName =
      String(form.receiver_first_name || "").trim();

    const lastName =
      String(form.receiver_last_name || "").trim();

    if (!firstName) {
      toast.error("نام گیرنده را وارد کنید.");
      return;
    }

    if (!lastName) {
      toast.error("نام خانوادگی گیرنده را وارد کنید.");
      return;
    }

    try {
      const {
        receiver_first_name,
        receiver_last_name,
        ...formPayload
      } = form;

      const payload = {
        ...formPayload,
        receiver_name: [
          receiver_first_name.trim(),
          receiver_last_name.trim()
        ].join(" "),
        is_default: Boolean(form.is_default),
      };

      const res = editingId
        ? await updateAddress({ id: editingId, ...payload }).unwrap()
        : await createAddress(payload).unwrap();

      toast.success(res?.message || "آدرس ذخیره شد.");
      resetForm();
    } catch (err) {
      toast.error(
        err?.data?.errors?.receiver_name?.[0] ||
          err?.data?.errors?.receiver_mobile?.[0] ||
          err?.data?.errors?.address?.[0] ||
          err?.data?.message ||
          "ذخیره آدرس انجام نشد."
      );
    }
  };

  const removeAddress = async (id) => {
    if (!window.confirm("این آدرس حذف شود؟")) {
      return;
    }

    try {
      const res = await deleteAddress(id).unwrap();
      toast.success(res?.message || "آدرس حذف شد.");
      if (editingId === id) {
        resetForm();
      }
    } catch (err) {
      toast.error(err?.data?.message || "حذف آدرس انجام نشد.");
    }
  };

  const makeDefault = async (id) => {
    try {
      const res = await setDefaultAddress(id).unwrap();
      toast.success(res?.message || "آدرس پیش‌فرض شد.");
    } catch (err) {
      toast.error(err?.data?.message || "تغییر آدرس پیش‌فرض انجام نشد.");
    }
  };

  return (
    <>
      <Nav />
      <Toaster position="top-right" reverseOrder={false} />

      <main className="account-page">
        <div className="my-container">
          <div className="account-shell">
            <AccountList />

            <section className="account-content">
              <div className="account-hero account-hero-compact">
                <div>
                  <span className="account-kicker">آدرس‌ها</span>
                  <h1>مدیریت آدرس‌های ارسال</h1>
                  <p>آدرس‌های مورد استفاده در checkout را همین‌جا مرتب نگه دارید.</p>
                </div>

                <button type="button" className="account-hero-action" onClick={() => setFormOpen((value) => !value)}>
                  <AiOutlinePlus size={18} />
                  آدرس جدید
                </button>
              </div>

              {formOpen && (
                <section className="account-card">
                  <div className="account-card-head">
                    <div>
                      <span>{editingId ? "ویرایش آدرس" : "آدرس جدید"}</span>
                      <h2>اطلاعات ارسال</h2>
                    </div>
                    <AiOutlineEnvironment size={25} />
                  </div>

                  <form className="account-address-form" onSubmit={submit}>
                    <label>
                      <span>نام *</span>

                      <input
                        value={form.receiver_first_name}
                        onChange={(e) =>
                          change(
                            "receiver_first_name",
                            e.target.value
                          )
                        }
                        placeholder="نام"
                        autoComplete="given-name"
                        required
                      />
                    </label>

                    <label>
                      <span>نام خانوادگی *</span>

                      <input
                        value={form.receiver_last_name}
                        onChange={(e) =>
                          change(
                            "receiver_last_name",
                            e.target.value
                          )
                        }
                        placeholder="نام خانوادگی"
                        autoComplete="family-name"
                        required
                      />
                    </label>

                    <label>
                      <span>موبایل</span>
                      <input
                        value={form.receiver_mobile}
                        onChange={(e) => change("receiver_mobile", e.target.value)}
                        placeholder="شماره موبایل"
                        inputMode="tel"
                      />
                    </label>

                    <label>
                      <span>استان</span>
                      <input value={form.province} onChange={(e) => change("province", e.target.value)} placeholder="استان" />
                    </label>

                    <label>
                      <span>شهر</span>
                      <input value={form.city} onChange={(e) => change("city", e.target.value)} placeholder="شهر" />
                    </label>

                    <label className="account-field-wide">
                      <span>آدرس</span>
                      <textarea value={form.address} onChange={(e) => change("address", e.target.value)} placeholder="نشانی کامل" />
                    </label>

                    <label>
                      <span>پلاک</span>
                      <input value={form.plaque} onChange={(e) => change("plaque", e.target.value)} placeholder="پلاک" />
                    </label>

                    <label>
                      <span>واحد</span>
                      <input value={form.unit} onChange={(e) => change("unit", e.target.value)} placeholder="واحد" />
                    </label>

                    <label>
                      <span>کد پستی</span>
                      <input value={form.postal_code} onChange={(e) => change("postal_code", e.target.value)} placeholder="کد پستی" />
                    </label>

                    <label className="account-check-row">
                      <input
                        type="checkbox"
                        checked={form.is_default}
                        onChange={(e) => change("is_default", e.target.checked)}
                      />
                      <span>آدرس پیش‌فرض باشد</span>
                    </label>

                    <div className="account-form-actions">
                      <button type="submit" disabled={saving}>
                        {saving ? "در حال ذخیره..." : editingId ? "ذخیره تغییرات" : "ثبت آدرس"}
                      </button>
                      <button type="button" onClick={resetForm}>
                        انصراف
                      </button>
                    </div>
                  </form>
                </section>
              )}

              <section className="account-card">
                <div className="account-card-head">
                  <div>
                    <span>آدرس‌های ذخیره‌شده</span>
                    <h2>{toPersianNumber(addresses.length)} آدرس</h2>
                  </div>
                  <AiOutlineEnvironment size={25} />
                </div>

                {isLoading || isFetching ? (
                  <Spinner />
                ) : isError ? (
                  <div className="account-alert">خطا در دریافت آدرس‌ها.</div>
                ) : addresses.length ? (
                  <div className="account-address-list">
                    {addresses.map((address) => (
                      <article key={address.id} className="account-address-card">
                        <div className="account-address-card-top">
                          <div>
                            <strong>{address.receiver_name}</strong>
                            <span>{address.receiver_mobile}</span>
                          </div>

                          {address.is_default && (
                            <em>
                              <AiOutlineCheckCircle size={17} />
                              پیش‌فرض
                            </em>
                          )}
                        </div>

                        <p>
                          {[address.province, address.city, address.address]
                            .filter(Boolean)
                            .join("، ")}
                        </p>

                        <div className="account-address-meta">
                          {address.plaque && <span>پلاک {address.plaque}</span>}
                          {address.unit && <span>واحد {address.unit}</span>}
                          {address.postal_code && <span>کد پستی {address.postal_code}</span>}
                        </div>

                        <div className="account-address-actions">
                          {!address.is_default && (
                            <button type="button" disabled={busy} onClick={() => makeDefault(address.id)}>
                              پیش‌فرض
                            </button>
                          )}
                          <button type="button" disabled={busy} onClick={() => editAddress(address)}>
                            <AiOutlineEdit size={17} />
                            ویرایش
                          </button>
                          <button type="button" disabled={busy} onClick={() => removeAddress(address.id)}>
                            <AiOutlineDelete size={17} />
                            حذف
                          </button>
                        </div>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="account-empty-state">
                    <AiOutlineEnvironment size={42} />
                    <strong>هنوز آدرسی ثبت نشده است</strong>
                    <p>برای سریع‌تر شدن خریدهای بعدی، یک آدرس ذخیره کنید.</p>
                    <button type="button" onClick={() => setFormOpen(true)}>
                      ثبت اولین آدرس
                    </button>
                  </div>
                )}
              </section>
            </section>
          </div>
        </div>
      </main>
    </>
  );
};

export default UserAddresses;
